import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../../lib/api";
import {
  Plus, Edit2, Trash2, X, Save, FileText, ChevronDown, ChevronRight,
  Folder, FolderOpen, Upload, Download, Image as ImageIcon, Archive,
  FolderPlus, Home, FolderUp, Eye, ExternalLink,
} from "lucide-react";
import { useConfirm } from "../../contexts/ConfirmContext";

const BASE = process.env.REACT_APP_BACKEND_URL + "/api";

function fileIcon(ct) {
  if (!ct) return <FileText size={14} />;
  if (ct.startsWith("image/")) return <ImageIcon size={14} />;
  if (ct.includes("zip") || ct.includes("tar")) return <Archive size={14} />;
  return <FileText size={14} />;
}

function formatSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1048576).toFixed(1)} MB`;
}

// ── File Preview Modal ───────────────────────────────────────────────
function isPreviewable(ct) {
  if (!ct) return false;
  return ct.startsWith("image/") || ct === "application/pdf" || ct.startsWith("text/") || ct === "application/json";
}

function PreviewModal({ file, onClose }) {
  const [textContent, setTextContent] = useState(null);
  const ct = file.content_type || "";
  const url = `${BASE}/files/${file._id}/view`;

  useEffect(() => {
    if (ct.startsWith("text/") || ct === "application/json") {
      fetch(url, { credentials: "include" })
        .then(r => r.text())
        .then(setTextContent)
        .catch(() => setTextContent("(Datei konnte nicht geladen werden)"));
    }
  }, [file._id, ct, url]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-card border border-border w-full max-w-5xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="file-preview-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-muted-foreground flex-shrink-0" />
            <h2 className="font-semibold text-sm truncate">{file.original_filename}</h2>
            <span className="text-xs font-mono text-muted-foreground flex-shrink-0">{formatSize(file.size)}</span>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <a href={`${BASE}/files/${file._id}/download`} target="_blank" rel="noreferrer"
              className="text-muted-foreground hover:text-primary p-1.5" title="Herunterladen">
              <Download size={14} />
            </a>
            <a href={url} target="_blank" rel="noreferrer"
              className="text-muted-foreground hover:text-primary p-1.5" title="In neuem Tab">
              <ExternalLink size={14} />
            </a>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1.5"><X size={16} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto bg-muted/30">
          {ct.startsWith("image/") && (
            <div className="flex items-center justify-center min-h-full p-4">
              <img src={url} alt={file.original_filename} className="max-w-full max-h-full object-contain" />
            </div>
          )}
          {ct === "application/pdf" && (
            <iframe src={url} className="w-full h-full" title={file.original_filename} />
          )}
          {(ct.startsWith("text/") || ct === "application/json") && (
            <pre className="p-4 text-xs font-mono whitespace-pre-wrap break-words">{textContent ?? "Lade..."}</pre>
          )}
          {!isPreviewable(ct) && (
            <div className="flex flex-col items-center justify-center h-full gap-3 p-8 text-center">
              <FileText size={48} className="text-muted-foreground" />
              <p className="text-sm font-semibold">Vorschau nicht verfügbar</p>
              <p className="text-xs text-muted-foreground">Dateityp: {ct || "unbekannt"}</p>
              <a href={`${BASE}/files/${file._id}/download`}
                className="mt-2 px-4 py-2 bg-primary text-primary-foreground text-xs font-semibold inline-flex items-center gap-1.5">
                <Download size={12} /> Herunterladen
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Doc Card ──────────────────────────────────────────────────────────
function DocCard({ doc, customerId, onDelete, onUpdate, onDragStart }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ title: doc.title, content: doc.content || "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put(`/customers/${customerId}/docs/${doc._id}`, form);
      onUpdate(r.data);
      setEditing(false);
    } finally { setSaving(false); }
  };

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, "doc", doc._id)}
      className="bg-card border border-border mb-2 cursor-grab active:cursor-grabbing"
      data-testid={`doc-card-${doc._id}`}
    >
      <div className="flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
           onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2 cursor-pointer flex-1 min-w-0">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <FileText size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="font-medium text-sm truncate">{doc.title}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-muted-foreground">{doc.updated_at ? new Date(doc.updated_at).toLocaleDateString("de-CH") : ""}</span>
          <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
            <button onClick={() => { setEditing(!editing); setExpanded(true); }} className="text-muted-foreground hover:text-primary p-1"><Edit2 size={13} /></button>
            <button onClick={() => onDelete(doc._id)} className="text-muted-foreground hover:text-destructive p-1"><Trash2 size={13} /></button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-border">
          {editing ? (
            <div className="mt-3 space-y-3">
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
              <textarea data-testid={`doc-content-${doc._id}`} value={form.content} rows={10}
                onChange={e => setForm({ ...form, content: e.target.value })}
                className="w-full px-3 py-2 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none resize-y" />
              <div className="flex gap-2">
                <button onClick={save} disabled={saving} className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold flex items-center gap-1"><Save size={12} />{saving ? "..." : "Speichern"}</button>
                <button onClick={() => { setEditing(false); setForm({ title: doc.title, content: doc.content || "" }); }} className="px-3 py-1.5 bg-secondary text-xs font-semibold flex items-center gap-1"><X size={12} />Abbrechen</button>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              {doc.content ? <pre className="text-sm whitespace-pre-wrap font-mono leading-relaxed">{doc.content}</pre>
                : <p className="text-sm text-muted-foreground italic">Kein Inhalt. Bearbeiten zum Hinzufügen.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── File Row ──────────────────────────────────────────────────────────
function FileRow({ file, onDelete, onDragStart, onPreview }) {
  const previewable = isPreviewable(file.content_type);
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, "file", file._id)}
      onClick={() => previewable && onPreview(file)}
      className={`flex items-center justify-between gap-2 px-4 py-2 bg-card border border-border mb-2 hover:bg-muted/30 ${previewable ? "cursor-pointer" : "cursor-grab"} active:cursor-grabbing`}
      data-testid={`file-row-${file._id}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <span className="text-muted-foreground flex-shrink-0">{fileIcon(file.content_type)}</span>
        <span className="font-medium text-sm truncate">{file.original_filename}</span>
        <span className="text-xs font-mono text-muted-foreground flex-shrink-0">{formatSize(file.size)}</span>
        <span className="text-xs text-muted-foreground flex-shrink-0 hidden md:inline">{file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString("de-CH") : ""}</span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
        {previewable && (
          <button onClick={() => onPreview(file)} className="text-muted-foreground hover:text-primary p-1" title="Vorschau"><Eye size={13} /></button>
        )}
        <button onClick={() => window.open(`${BASE}/files/${file._id}/download`, "_blank")} className="text-muted-foreground hover:text-primary p-1" title="Herunterladen"><Download size={13} /></button>
        <button onClick={() => onDelete(file._id)} className="text-muted-foreground hover:text-destructive p-1" title="Löschen"><Trash2 size={13} /></button>
      </div>
    </div>
  );
}

// ── Folder list item (left sidebar) ───────────────────────────────────
function FolderItem({ folder, active, onSelect, onRename, onDelete, onDrop, onDragOver }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(folder.name);
  const [over, setOver] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (name.trim() && name.trim() !== folder.name) {
      await onRename(folder._id, name.trim());
    }
    setEditing(false);
  };

  return (
    <div
      onClick={() => !editing && onSelect(folder._id)}
      onDragOver={e => { e.preventDefault(); setOver(true); onDragOver?.(); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { setOver(false); onDrop(e, folder._id); }}
      data-testid={`folder-${folder._id}`}
      className={`flex items-center gap-2 px-3 py-2 cursor-pointer rounded-sm transition-colors group ${
        active ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
      } ${over ? "ring-2 ring-primary/50 bg-primary/5" : ""}`}
    >
      {active ? <FolderOpen size={14} /> : <Folder size={14} />}
      {editing ? (
        <form onSubmit={submit} className="flex-1">
          <input autoFocus value={name} onChange={e => setName(e.target.value)} onBlur={submit}
            className="w-full px-1 py-0 bg-background border border-primary text-sm focus:outline-none" />
        </form>
      ) : (
        <>
          <span className="flex-1 text-sm font-medium truncate" data-testid={`folder-name-${folder._id}`}>{folder.name}</span>
          <span className="text-xs text-muted-foreground tabular-nums">{(folder.doc_count || 0) + (folder.file_count || 0)}</span>
          <div className="hidden group-hover:flex items-center gap-0.5 ml-1" onClick={e => e.stopPropagation()}>
            <button onClick={() => setEditing(true)} className="text-muted-foreground hover:text-primary p-0.5"><Edit2 size={11} /></button>
            <button onClick={() => onDelete(folder._id)} className="text-muted-foreground hover:text-destructive p-0.5"><Trash2 size={11} /></button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Main DocsTab ──────────────────────────────────────────────────────
export default function DocsTab({ customerId }) {
  const confirm = useConfirm();
  const [folders, setFolders] = useState([]);
  const [docs, setDocs] = useState([]);
  const [files, setFiles] = useState([]);
  const [activeFolder, setActiveFolder] = useState("root"); // "root" or folder_id
  const [showDocForm, setShowDocForm] = useState(false);
  const [docForm, setDocForm] = useState({ title: "", content: "" });
  const [newFolderName, setNewFolderName] = useState("");
  const [showFolderForm, setShowFolderForm] = useState(false);
  const fileInput = useRef();
  const folderInput = useRef();
  const [rootDragOver, setRootDragOver] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [uploading, setUploading] = useState({ active: false, current: 0, total: 0 });

  const load = useCallback(async () => {
    const params = { folder_id: activeFolder === "root" ? "root" : activeFolder };
    const [foldersR, docsR, filesR] = await Promise.all([
      api.get(`/customers/${customerId}/folders`),
      api.get(`/customers/${customerId}/docs`, { params }),
      api.get(`/customers/${customerId}/files`, { params }),
    ]);
    setFolders(foldersR.data);
    setDocs(docsR.data);
    setFiles(filesR.data);
  }, [customerId, activeFolder]);

  useEffect(() => { load(); }, [load]);

  // ── Folder ops ──────────────────────────────────────────────────
  const createFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim()) return;
    await api.post(`/customers/${customerId}/folders`, { name: newFolderName.trim() });
    setNewFolderName("");
    setShowFolderForm(false);
    load();
  };

  const renameFolder = async (id, name) => {
    await api.put(`/customers/${customerId}/folders/${id}`, { name });
    load();
  };

  const deleteFolder = async (id) => {
    if (!(await confirm({ title: "Ordner löschen", message: "Der Ordner wird gelöscht, die Inhalte werden in die Wurzel verschoben.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/customers/${customerId}/folders/${id}`);
    if (activeFolder === id) setActiveFolder("root");
    load();
  };

  // ── Drag & Drop ─────────────────────────────────────────────────
  const onDragStart = (e, kind, id) => {
    e.dataTransfer.setData("kind", kind);
    e.dataTransfer.setData("id", id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    const kind = e.dataTransfer.getData("kind");
    const id = e.dataTransfer.getData("id");
    if (!kind || !id) return;
    const targetFolder = folderId === "root" ? null : folderId;
    const path = kind === "doc"
      ? `/customers/${customerId}/docs/${id}/move`
      : `/customers/${customerId}/files/${id}/move`;
    await api.put(path, { folder_id: targetFolder });
    load();
  };

  // ── Doc / File ops ──────────────────────────────────────────────
  const createDoc = async (e) => {
    e.preventDefault();
    const folderId = activeFolder === "root" ? null : activeFolder;
    const r = await api.post(`/customers/${customerId}/docs`, { ...docForm, folder_id: folderId });
    setDocs([r.data, ...docs]);
    setDocForm({ title: "", content: "" });
    setShowDocForm(false);
  };

  const deleteDoc = async (id) => {
    if (!(await confirm({ title: "Dokument löschen", message: "Das Dokument wird gelöscht, Anhänge werden in die Wurzel verschoben.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/customers/${customerId}/docs/${id}`);
    setDocs(docs.filter(d => d._id !== id));
  };

  const updateDoc = (updated) => setDocs(docs.map(d => d._id === updated._id ? updated : d));

  const uploadFile = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (fileList.length === 0) return;

    // Detect if it's a folder upload (webkitRelativePath set) → create sub-folders by path
    const isFolderUpload = fileList.some(f => f.webkitRelativePath);
    const folderCache = {}; // path → folder_id

    setUploading({ active: true, current: 0, total: fileList.length });

    for (let i = 0; i < fileList.length; i++) {
      const f = fileList[i];
      let targetFolderId = activeFolder === "root" ? null : activeFolder;

      if (isFolderUpload && f.webkitRelativePath) {
        // Path: "MeinOrdner/Unterordner/datei.pdf" → create "MeinOrdner" + "Unterordner" if needed
        const parts = f.webkitRelativePath.split("/").slice(0, -1);
        if (parts.length > 0) {
          // Use only the top-level folder name (flat) — keep simple
          const topName = parts[0];
          if (!folderCache[topName]) {
            try {
              const r = await api.post(`/customers/${customerId}/folders`, { name: topName });
              folderCache[topName] = r.data._id;
            } catch {
              // Folder might already exist — try fetching
              const fr = await api.get(`/customers/${customerId}/folders`);
              const existing = fr.data.find(x => x.name === topName);
              folderCache[topName] = existing ? existing._id : null;
            }
          }
          if (folderCache[topName]) targetFolderId = folderCache[topName];
        }
      }

      try {
        const fd = new FormData();
        fd.append("file", f);
        const url = `/customers/${customerId}/files${targetFolderId ? `?folder_id=${targetFolderId}` : ""}`;
        await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } catch (err) {
        console.error("Upload failed for", f.name, err);
      }
      setUploading(s => ({ ...s, current: i + 1 }));
    }

    setUploading({ active: false, current: 0, total: 0 });
    if (fileInput.current) fileInput.current.value = "";
    if (folderInput.current) folderInput.current.value = "";
    load();
  };

  const deleteFile = async (id) => {
    if (!(await confirm({ title: "Datei löschen", message: "Diese Datei wird dauerhaft entfernt.", confirmLabel: "Löschen" }))) return;
    await api.delete(`/files/${id}`);
    setFiles(files.filter(f => f._id !== id));
  };

  const activeFolderName = activeFolder === "root"
    ? "Wurzel"
    : folders.find(f => f._id === activeFolder)?.name || "Ordner";

  const totalItems = docs.length + files.length;

  return (
    <div className="animate-in flex gap-6">
      {/* ── Folder Sidebar ────────────────────────────────────────── */}
      <aside className="w-64 flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Ordner</h3>
          <button
            data-testid="add-folder-button"
            onClick={() => setShowFolderForm(!showFolderForm)}
            className="text-muted-foreground hover:text-primary"
            title="Neuer Ordner"
          >
            <FolderPlus size={14} />
          </button>
        </div>

        {showFolderForm && (
          <form onSubmit={createFolder} className="mb-2 flex gap-1">
            <input
              data-testid="folder-name-input"
              autoFocus value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              placeholder="Ordnername"
              className="flex-1 px-2 py-1 text-sm bg-background border border-input focus:border-primary focus:outline-none"
            />
            <button type="submit" data-testid="folder-create-submit" className="px-2 py-1 bg-primary text-primary-foreground text-xs"><Plus size={11} /></button>
          </form>
        )}

        {/* Root */}
        <div
          onClick={() => setActiveFolder("root")}
          onDragOver={e => { e.preventDefault(); setRootDragOver(true); }}
          onDragLeave={() => setRootDragOver(false)}
          onDrop={e => { setRootDragOver(false); handleDrop(e, "root"); }}
          data-testid="folder-root"
          className={`flex items-center gap-2 px-3 py-2 cursor-pointer rounded-sm transition-colors ${
            activeFolder === "root" ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
          } ${rootDragOver ? "ring-2 ring-primary/50 bg-primary/5" : ""}`}
        >
          <Home size={14} />
          <span className="flex-1 text-sm font-medium">Wurzel</span>
        </div>

        {/* Folder list */}
        <div className="mt-1 space-y-0.5" data-testid="folder-list">
          {folders.map(f => (
            <FolderItem
              key={f._id}
              folder={f}
              active={activeFolder === f._id}
              onSelect={setActiveFolder}
              onRename={renameFolder}
              onDelete={deleteFolder}
              onDrop={handleDrop}
            />
          ))}
        </div>

        {folders.length === 0 && !showFolderForm && (
          <p className="text-xs text-muted-foreground italic mt-2 px-3">Keine Ordner. Klicke auf <FolderPlus size={11} className="inline" /></p>
        )}
      </aside>

      {/* ── Main Content ─────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
              {activeFolderName}
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">{totalItems} Einträge — Items per Drag &amp; Drop in Ordner ziehen</p>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button data-testid="add-doc-button" onClick={() => setShowDocForm(!showDocForm)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90">
              <Plus size={12} /> Dokument
            </button>
            <button data-testid="upload-file-button" onClick={() => fileInput.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary text-xs font-semibold hover:bg-secondary/80">
              <Upload size={12} /> Datei(en)
            </button>
            <button data-testid="upload-folder-button" onClick={() => folderInput.current?.click()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary text-xs font-semibold hover:bg-secondary/80"
              title="Ganzen Ordner hochladen (legt automatisch Sub-Ordner an)">
              <FolderUp size={12} /> Ordner
            </button>
            <input ref={fileInput} type="file" multiple className="hidden" onChange={uploadFile} />
            <input ref={folderInput} type="file" multiple webkitdirectory="" directory="" className="hidden" onChange={uploadFile} />
          </div>
        </div>

        {showDocForm && (
          <form onSubmit={createDoc} className="bg-card border border-border p-4 mb-4" data-testid="create-doc-form">
            <div className="space-y-3">
              <input data-testid="doc-form-title" value={docForm.title}
                onChange={e => setDocForm({ ...docForm, title: e.target.value })}
                required placeholder="Titel"
                className="w-full px-3 py-2 text-sm bg-background border border-input focus:border-primary focus:outline-none" />
              <textarea data-testid="doc-form-content" value={docForm.content}
                onChange={e => setDocForm({ ...docForm, content: e.target.value })}
                rows={6} placeholder="Dokumentation, Anleitung, Konfiguration..."
                className="w-full px-3 py-2 text-sm font-mono bg-background border border-input focus:border-primary focus:outline-none resize-y" />
            </div>
            <div className="flex gap-2 mt-3">
              <button data-testid="save-doc-button" type="submit" className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-semibold">Speichern</button>
              <button type="button" onClick={() => setShowDocForm(false)} className="px-3 py-1.5 bg-secondary text-xs font-semibold">Abbrechen</button>
            </div>
          </form>
        )}

        {totalItems === 0 ? (
          <div className="p-12 text-center text-sm text-muted-foreground bg-card border border-dashed border-border">
            Keine Einträge in diesem Ordner. Erstelle ein Dokument, lade eine Datei hoch oder ziehe Inhalte hierher.
          </div>
        ) : (
          <>
            {docs.map(d => (
              <DocCard key={d._id} doc={d} customerId={customerId}
                onDelete={deleteDoc} onUpdate={updateDoc} onDragStart={onDragStart} />
            ))}
            {files.map(f => (
              <FileRow key={f._id} file={f} onDelete={deleteFile} onDragStart={onDragStart} onPreview={setPreviewFile} />
            ))}
          </>
        )}
      </div>

      {previewFile && <PreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />}

      {uploading.active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border p-6 max-w-sm text-center min-w-[320px]">
            <Upload className="mx-auto mb-3 text-primary animate-pulse" size={28} />
            <p className="font-semibold mb-1">Lade Dateien hoch</p>
            <p className="text-xs text-muted-foreground mb-3">{uploading.current} / {uploading.total}</p>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary transition-all" style={{ width: `${(uploading.current / uploading.total) * 100}%` }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
